/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { Anchor, Button, Modal, StatusBadge, Text, TextInput, Title, UnstyledButton } from '@/shared/ui';
import { formatLocalizedDate } from '@/shared/utils/date';
import { NetworkRow, Toggle } from '../FacebookSettingsSection/FacebookSettingsParts';
import type { PortalSettings } from './constants';
import { UrlField } from './CanalProBlock';
import { ConfigButton } from './widgets';
import { CNM_SUPPORT_EMAIL, ChavesNaMaoEmailModal, cnmEmail, type CnmEmailKind } from './ChavesNaMaoEmailModal';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const CNM_PORTAL = 'chavesnamao';
/** The portal's red. */
export const CNM_RED = '#E30613';
/** "Meus Dados" in the advertiser's account; the lead token is under "Dados Adicionais" (login first if needed). */
export const CNM_ACCOUNT_URL = 'https://www.chavesnamao.com.br/conta/perfil/';

/** Whether the agency has Chaves na Mão on. */
export function cnmEnabled(settings: PortalSettings): boolean {
  return settings.enabled_portals.includes(CNM_PORTAL);
}

export interface ChavesNaMaoBlockProps {
  settings: PortalSettings;
  saving: boolean;
  onToggle: () => void;
  /** Generates a new private feed URL and resolves with it (shown once). */
  onCreateFeedUrl: () => Promise<string | null>;
  /** Saves the account e-mail and lead token; resolves with the webhook URL. */
  onSaveLeadCredentials: (email: string, token: string) => Promise<string | null>;
  /** The agency's name and CPF/CNPJ, filled into the e-mails to Chaves na Mão. */
  agency: AgencyIdentity;
  /** Whether the settings modal is open (the section opens it on connect). */
  configOpen: boolean;
  onConfigOpenChange: (open: boolean) => void;
  t: Translate;
}

/** Who the agency is, for the e-mails to Chaves na Mão. */
export interface AgencyIdentity {
  name: string;
  document: string;
}

/** The agency plus its Chaves na Mão account e-mail, which the support team finds the account by. */
type CnmSender = AgencyIdentity & { account: string };

/** A plausible e-mail address (the portal checks the real one). */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Chaves na Mão: a private XML feed with the properties the brokers publish
 * there, and a webhook that brings the portal's contacts in as CRM clients.
 * Unlike Canal Pro, Housien sees when the portal reads the feed.
 */
export function ChavesNaMaoBlock({
  settings, saving, onToggle, onCreateFeedUrl, onSaveLeadCredentials, agency, configOpen, onConfigOpenChange, t,
}: ChavesNaMaoBlockProps) {
  if (!cnmEnabled(settings)) {
    // Off: one row with a connect button, like Canal Pro and the social networks.
    return (
      <div data-testid="chaves-na-mao-block">
        <NetworkRow
          testId="network-row-chaves-na-mao"
          icon={<ChavesNaMaoBadge />}
          title={t('chavesNaMao.titulo')}
          hint={t('chavesNaMao.dicaConectar')}
          trailing={(
            <Button variant="outlined" size="xs" data-testid="chaves-na-mao-connect-btn" disabled={saving} onClick={onToggle}>
              {t('chavesNaMao.conectar')}
            </Button>
          )}
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-(--space-4)" data-testid="chaves-na-mao-block">
      <NetworkRow
        testId="network-row-chaves-na-mao"
        icon={<ChavesNaMaoBadge />}
        title={t('chavesNaMao.titulo')}
        identity={t('chavesNaMao.descricao')}
        trailing={<Toggle checked testId="toggle-chaves-na-mao" disabled={saving} onClick={onToggle} />}
        meta={(
          <>
            <StatusBadges settings={settings} t={t} />
            <ConfigButton
              onClick={() => onConfigOpenChange(true)}
              label={t('portal.configuracoes')} testId="chaves-na-mao-config-btn"
            />
          </>
        )}
      />
      <Modal
        opened={configOpen}
        onClose={() => onConfigOpenChange(false)}
        title={t('chavesNaMao.configuracoesTitulo')}
        size="2xl"
      >
        <div className="flex flex-col gap-(--space-4) max-h-[70vh] overflow-y-auto" data-testid="chaves-na-mao-settings">
          <ConnectionSteps
            settings={settings} saving={saving} onCreateFeedUrl={onCreateFeedUrl}
            onSaveLeadCredentials={onSaveLeadCredentials} agency={agency} t={t}
          />
          <CnmStep number={3} title={t('chavesNaMao.passoPublicar')} hint={t('chavesNaMao.passoPublicarOnde')} />
          <Text className="m-0 text-xs text-(--text-tertiary) leading-relaxed">{t('chavesNaMao.requisitos')}</Text>
        </div>
      </Modal>
    </div>
  );
}

/** The Chaves na Mão status badges: the last feed read and listed-ad count. */
function StatusBadges({ settings, t }: { settings: PortalSettings; t: Translate }) {
  const readAt = formatLocalizedDate(settings.last_cnm_read_at);
  const listed = settings.cnm_listed_ids?.length ?? 0;
  return (
    <>
      {readAt
        ? <StatusBadge tone="success">{t('chavesNaMao.lidoEm', { date: readAt })}</StatusBadge>
        : <StatusBadge tone="info">{t('chavesNaMao.aguardandoLeitura')}</StatusBadge>}
      <StatusBadge tone="neutral">{t('chavesNaMao.publicados', { count: listed })}</StatusBadge>
      {settings.cnm_lead_saved_at && <StatusBadge tone="neutral">{t('chavesNaMao.leadsConfigurados')}</StatusBadge>}
    </>
  );
}

/**
 * Steps 1 and 2. The account e-mail is asked once, in step 1: the feed e-mail
 * needs it (it is how Chaves na Mão finds the account) and step 2 reuses it as
 * the lead credentials' e-mail.
 */
function ConnectionSteps({ settings, saving, onCreateFeedUrl, onSaveLeadCredentials, agency, t }: {
  settings: PortalSettings; saving: boolean; onCreateFeedUrl: () => Promise<string | null>;
  onSaveLeadCredentials: (email: string, token: string) => Promise<string | null>; agency: AgencyIdentity; t: Translate;
}) {
  const [account, setAccount] = useState(settings.cnm_lead_email ?? '');
  const sender: CnmSender = { ...agency, account: account.trim() };
  return (
    <>
      <FeedStep settings={settings} saving={saving} onCreate={onCreateFeedUrl} sender={sender} onAccount={setAccount} t={t} />
      <LeadsStep settings={settings} saving={saving} onSave={onSaveLeadCredentials} sender={sender} t={t} />
    </>
  );
}

/** Step that creates the Chaves na Mão XML feed link and shows it once. */
function FeedStep({ settings, saving, onCreate, sender, onAccount, t }: {
  settings: PortalSettings; saving: boolean; onCreate: () => Promise<string | null>; sender: CnmSender;
  onAccount: (value: string) => void; t: Translate;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const createdAt = formatLocalizedDate(settings.cnm_feed_url_created_at);
  const create = async () => {
    setBusy(true);
    try {
      const created = await onCreate();
      setUrl(created);
      setModalOpen(!!created);
    } finally {
      setBusy(false);
    }
  };
  return (
    <CnmStep number={1} title={t('chavesNaMao.passoFeed')} hint={t('chavesNaMao.passoFeedOnde', { email: CNM_SUPPORT_EMAIL })}>
      <div className="w-full max-w-[360px]">
        <TextInput
          name="cnm-account-email" type="email" label={t('chavesNaMao.emailConta')} value={sender.account}
          description={t('chavesNaMao.emailContaDica')}
          onChange={(e) => onAccount(e.target.value)} data-testid="chaves-na-mao-account-email"
        />
      </div>
      {url ? (
        <>
          <ShownOnce url={url} testId="chaves-na-mao-feed-url" t={t} />
          <EmailButton kind="feed" url={url} open={modalOpen} setOpen={setModalOpen} sender={sender} t={t} />
        </>
      ) : (
        <div className="flex flex-col gap-(--space-1-5)">
          {createdAt && <Text className="m-0 text-xs text-(--text-secondary)">{t('chavesNaMao.feedGerado', { date: createdAt })}</Text>}
          <ActionButton
            testId="chaves-na-mao-create-feed-url"
            disabled={busy || saving || !looksLikeEmail(sender.account)}
            onClick={create}
          >
            {createdAt ? t('chavesNaMao.gerarNovoLink') : t('chavesNaMao.gerarLink')}
          </ActionButton>
        </div>
      )}
    </CnmStep>
  );
}

/** Step that saves the Chaves na Mão lead e-mail and token and shows the webhook link once. */
function LeadsStep({ settings, saving, onSave, sender, t }: {
  settings: PortalSettings; saving: boolean; onSave: (email: string, token: string) => Promise<string | null>;
  sender: CnmSender; t: Translate;
}) {
  const [token, setToken] = useState('');
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const saved = await onSave(sender.account, token.trim());
      if (saved) setToken('');
      setUrl(saved);
      setModalOpen(!!saved);
    } finally {
      setBusy(false);
    }
  };
  return (
    <CnmStep number={2} title={t('chavesNaMao.passoLeads')} hint={t('chavesNaMao.passoLeadsOnde')}>
      <ExternalLink href={CNM_ACCOUNT_URL} testId="chaves-na-mao-account-link" label={t('chavesNaMao.abrirMeusDados')} newTab />
      <div className="w-full max-w-[360px]">
        <TextInput
          name="cnm-lead-token" type="password" label={t('chavesNaMao.tokenLeads')} value={token}
          placeholder={settings.cnm_lead_saved_at ? t('chavesNaMao.tokenSalvo') : ''}
          onChange={(e) => setToken(e.target.value)} data-testid="chaves-na-mao-lead-token"
        />
      </div>
      <ActionButton
        testId="chaves-na-mao-save-leads"
        disabled={busy || saving || !looksLikeEmail(sender.account) || !token.trim()}
        onClick={save}
      >
        {t('chavesNaMao.salvarLeads')}
      </ActionButton>
      {url && (
        <>
          <UrlField value={url} testId="chaves-na-mao-lead-url" t={t} />
          <EmailButton kind="leads" url={url} open={modalOpen} setOpen={setModalOpen} sender={sender} t={t} />
        </>
      )}
    </CnmStep>
  );
}

/** Reopens the ready-to-send e-mail (it opens by itself right after the link is generated). */
function EmailButton({ kind, url, open, setOpen, sender, t }: {
  kind: CnmEmailKind; url: string; open: boolean; setOpen: (open: boolean) => void; sender: CnmSender; t: Translate;
}) {
  return (
    <>
      <ActionButton testId={`chaves-na-mao-${kind}-email-btn`} disabled={false} onClick={() => setOpen(true)}>
        {t('chavesNaMao.email.verEmail')}
      </ActionButton>
      <ChavesNaMaoEmailModal opened={open} onClose={() => setOpen(false)} kind={kind} email={cnmEmail(kind, url, sender, t)} t={t} />
    </>
  );
}

/** A text link to an outside page, optionally opening in a new tab. */
function ExternalLink({ href, testId, label, newTab = false }: { href: string; testId: string; label: string; newTab?: boolean }) {
  return (
    <Anchor
      href={href}
      data-testid={testId}
      {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})} // i18n-exempt: rel attribute value, not copy
      className="self-start text-xs font-semibold text-[var(--seed-blue)] hover:underline"
    >
      {label}
    </Anchor>
  );
}

/** A freshly created link with the warning that it is shown only once. */
function ShownOnce({ url, testId, t }: { url: string; testId: string; t: Translate }) {
  return (
    <>
      <UrlField value={url} testId={testId} t={t} />
      <Text className="m-0 text-xs text-[var(--status-warning-text)]">{t('chavesNaMao.linkUmaVez')}</Text>
    </>
  );
}

/** The block's primary action button. */
function ActionButton({ testId, disabled, onClick, children }: {
  testId: string; disabled: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="self-start text-xs font-semibold px-(--space-3) py-(--space-1-5) rounded-(--radius-sm) border border-(--border-subtle) bg-[var(--surface-primary)] text-(--text-primary) cursor-pointer disabled:opacity-60"
    >
      {children}
    </UnstyledButton>
  );
}

/** One numbered Chaves na Mão setup step, with its title, hint and content. */
function CnmStep({ number, title, hint, children }: { number: number; title: string; hint: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-(--space-2) p-(--space-3) rounded-(--radius-md) border border-(--border-subtle)" data-testid={`chaves-na-mao-step-${number}`}>
      <Title order={4} size="sm" className="m-0 font-semibold text-(--text-primary)">{`${number}. ${title}`}</Title>
      <Text className="m-0 text-xs text-(--text-secondary) leading-relaxed">{hint}</Text>
      {children}
    </div>
  );
}

/**
 * The Chaves na Mão mark (portal red) in a circle.
 *
 * @param props - Size in pixels.
 * @returns The badge.
 */
export function ChavesNaMaoBadge({ size = 40 }: { size?: number }) {
  return (
    <Text
      component="span"
      aria-hidden="true"
      className="inline-flex items-center justify-center rounded-full font-extrabold shrink-0"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.32), background: CNM_RED, color: 'var(--text-inverse)' }}
    >
      CM
    </Text>
  );
}
